import {
  inventoryBoxShortCodeFromHex,
  parseInventoryBoxIdFromScan,
  parseLegacyInventoryAdjustmentUrl,
} from "@integrator-src/api/inventory-box/inventory-box-scan";

import type { KittingPickLine } from "./kittingPickModel";
import {
  resolveKittingCartScanToSlotKey,
  type KittingCartScanResolution,
} from "./kittingCartMarks";

export type { KittingCartScanResolution };

export function normalizeKittingScannedSku(raw: string): string {
  return raw.trim();
}

export type KittingMaterialScanTarget =
  | { kind: "box"; hex: string }
  | { kind: "sku"; sku: string };

/**
 * Inventory carton Data Matrix is a hub URL. A bare 32-hex id is also a carton
 * on this page. A URL that is not a carton stays unresolved. Everything else is a SKU.
 */
export function kittingMaterialScanTarget(raw: string): KittingMaterialScanTarget | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const boxHex = parseInventoryBoxIdFromScan(trimmed);
  if (boxHex != null) return { kind: "box", hex: boxHex };
  const legacy = parseLegacyInventoryAdjustmentUrl(trimmed);
  if (legacy != null) return { kind: "sku", sku: legacy.sku };
  if (/^https?:\/\//iu.test(trimmed)) return null;
  if (!trimmed.includes("/") && /^[0-9a-f]{32}$/iu.test(trimmed)) {
    return { kind: "box", hex: trimmed.toLowerCase() };
  }
  return { kind: "sku", sku: trimmed };
}

export function kittingPickLinesMatchingSku(
  pickLines: readonly KittingPickLine[],
  scannedSku: string,
): KittingPickLine[] {
  const needle = normalizeKittingScannedSku(scannedSku).toLowerCase();
  if (needle.length === 0) return [];
  return pickLines.filter((line) => line.sku.trim().toLowerCase() === needle);
}

export function kittingPickLinesMatchingSkuAtLocation(
  pickLines: readonly KittingPickLine[],
  scannedSku: string,
  storageLocation: string,
): KittingPickLine[] {
  return kittingPickLinesMatchingSku(pickLines, scannedSku).filter(
    (line) => line.storageLocation === storageLocation,
  );
}

/** Quantity drafts are at most six digits. Anything else in that buffer is a scan. */
export function kittingBufferedTextIsScan(buffer: string): boolean {
  if (buffer.trim() === "") return false;
  if (kittingMaterialScanTarget(buffer)?.kind === "box") return true;
  if (/[^0-9]/u.test(buffer)) return true;
  return buffer.length > 6;
}

export function kittingContainerLabelFromHexes(hexes: readonly string[]): string {
  return hexes.map((hex) => inventoryBoxShortCodeFromHex(hex)).join(", ");
}

export function kittingPickContainersFromHexes(
  hexes: readonly string[],
  quantity: number,
): { hex: string; shortCode: string; quantity: number }[] {
  const unique = [...new Set(hexes.map((hex) => hex.trim().toLowerCase()))].filter((hex) => hex.length === 32);
  if (unique.length === 0 || quantity <= 0) return [];
  if (unique.length === 1) {
    return [{
      hex: unique[0]!,
      shortCode: inventoryBoxShortCodeFromHex(unique[0]!),
      quantity,
    }];
  }
  return unique.map((hex) => ({
    hex,
    shortCode: inventoryBoxShortCodeFromHex(hex),
    quantity: 1,
  }));
}

export type KittingQuantityModeKeyPlan =
  | { action: "ignore"; buffer: string }
  | { action: "digit"; buffer: string; digit: string }
  | { action: "accumulate-scan"; buffer: string }
  | { action: "submit-scan"; buffer: string; scanned: string }
  | { action: "submit-quantity"; buffer: string }
  | { action: "backspace-quantity"; buffer: string }
  | { action: "backspace-scan"; buffer: string };

/**
 * After a SKU and cart bin are selected, the numpad owns short numbers.
 * A container or SKU wedge still has to arrive as a scan, not as those digits.
 */
export function planKittingQuantityModeKey(buffer: string, key: string): KittingQuantityModeKeyPlan {
  if (key === "Enter") {
    if (kittingBufferedTextIsScan(buffer)) {
      return { action: "submit-scan", buffer: "", scanned: buffer };
    }
    return { action: "submit-quantity", buffer: "" };
  }
  if (key === "Backspace") {
    const next = buffer.slice(0, -1);
    if (kittingBufferedTextIsScan(buffer)) {
      return { action: "backspace-scan", buffer: next };
    }
    return { action: "backspace-quantity", buffer: next };
  }
  if (key.length !== 1) {
    return { action: "ignore", buffer };
  }
  const next = `${buffer}${key}`;
  if (kittingBufferedTextIsScan(next)) {
    return { action: "accumulate-scan", buffer: next };
  }
  if (/^\d$/u.test(key)) {
    return { action: "digit", buffer: next, digit: key };
  }
  return { action: "accumulate-scan", buffer: next };
}

export function parseKittingQuantityDraft(draft: string): number | null {
  const trimmed = draft.trim();
  if (trimmed.length === 0) return null;
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

export interface KittingDestinationDisplayLines {
  primary: string;
  secondary: string | null;
}

/** Splits a destination headline into the job and station lines used by compact picker tiles. */
export function kittingDestinationDisplayLines(destinationLabel: string): KittingDestinationDisplayLines {
  const trimmed = destinationLabel.trim();
  const separatorIndex = trimmed.indexOf(" · ");
  if (separatorIndex < 0) {
    return { primary: trimmed, secondary: null };
  }
  const primary = trimmed.slice(0, separatorIndex).trim();
  const secondary = trimmed.slice(separatorIndex + 3).trim();
  return {
    primary: primary || trimmed,
    secondary: secondary || null,
  };
}

export interface KittingPickSelectionRefresh {
  selectedLine: KittingPickLine | null;
  candidateLines: KittingPickLine[];
  inputMode: "scan" | "quantity";
}

/** Keeps the selected destination visible without discarding other matching destinations. */
export function kittingPickerCandidatesWithSelectedLine(
  currentCandidateLines: readonly KittingPickLine[],
  selectedLine: KittingPickLine,
): KittingPickLine[] {
  return currentCandidateLines.some((line) => line.id === selectedLine.id)
    ? [...currentCandidateLines]
    : [selectedLine];
}

/**
 * Lines rendered in the order/bin picker. A directly selected sole match must
 * remain visible even when candidate state was cleared during auto-selection.
 */
export function kittingPickerDisplayLines(
  candidateLines: readonly KittingPickLine[],
  selectedLine: KittingPickLine | null,
): KittingPickLine[] {
  if (candidateLines.length > 0) {
    return [...candidateLines];
  }
  return selectedLine == null ? [] : [selectedLine];
}

function kittingPickableLines(lines: readonly KittingPickLine[]): KittingPickLine[] {
  return lines.filter((line) => line.qtyDue > 0);
}

/**
 * Reconciles the active picker after refreshed demand removes or updates lines.
 * A full bin stays in the list. A different open bin is not selected for the picker.
 */
export function kittingRefreshPickSelection(
  scopedPickLines: readonly KittingPickLine[],
  selectedLine: KittingPickLine,
  currentCandidateLines: readonly KittingPickLine[],
): KittingPickSelectionRefresh {
  const refreshed = scopedPickLines.find((line) => line.id === selectedLine.id);
  if (refreshed != null && refreshed.qtyDue > 0) {
    const locationScoped = currentCandidateLines.every(
      (line) => line.storageLocation === selectedLine.storageLocation,
    );
    const candidateLines = locationScoped
      ? kittingPickLinesMatchingSkuAtLocation(
          scopedPickLines,
          selectedLine.sku,
          selectedLine.storageLocation,
        )
      : kittingPickLinesMatchingSku(scopedPickLines, selectedLine.sku);
    return { selectedLine: refreshed, candidateLines, inputMode: "quantity" };
  }

  const matches = kittingPickLinesMatchingSkuAtLocation(
    scopedPickLines,
    selectedLine.sku,
    selectedLine.storageLocation,
  );
  const pickable = kittingPickableLines(matches);
  if (pickable.length === 0) {
    return { selectedLine: null, candidateLines: [], inputMode: "scan" };
  }
  return { selectedLine: null, candidateLines: matches, inputMode: "scan" };
}

export interface KittingOpenPickSelection {
  selectedLine: KittingPickLine | null;
  selectedSku: string | null;
  selectedStorageLocation: string | null;
  candidateLines: KittingPickLine[];
  inputMode: "scan" | "quantity";
}

/**
 * After demand refresh, drop the quantity list when the selected SKU has nothing left to pick.
 * A fully picked SKU leaves the route, so the order tiles and the SKU selection go with it.
 */
export function kittingReconcileOpenPickSelection(params: {
  scopedPickLines: readonly KittingPickLine[];
  selectedLine: KittingPickLine | null;
  selectedSku: string | null;
  selectedStorageLocation: string | null;
  candidateLines: readonly KittingPickLine[];
}): KittingOpenPickSelection | null {
  const sku = params.selectedLine?.sku ?? params.selectedSku;
  if (sku == null) {
    if (params.candidateLines.length === 0) return null;
    return {
      selectedLine: null,
      selectedSku: null,
      selectedStorageLocation: null,
      candidateLines: [],
      inputMode: "scan",
    };
  }

  const location = params.selectedLine?.storageLocation ?? params.selectedStorageLocation;
  const openForSelection =
    location != null
      ? kittingPickLinesMatchingSkuAtLocation(params.scopedPickLines, sku, location)
      : kittingPickLinesMatchingSku(params.scopedPickLines, sku);
  if (openForSelection.length === 0) {
    return {
      selectedLine: null,
      selectedSku: null,
      selectedStorageLocation: null,
      candidateLines: [],
      inputMode: "scan",
    };
  }

  if (params.selectedLine == null) {
    const stillOpen = params.candidateLines
      .map((line) => openForSelection.find((open) => open.id === line.id))
      .filter((line): line is KittingPickLine => line != null);
    return {
      selectedLine: null,
      selectedSku: sku,
      selectedStorageLocation: location,
      candidateLines: stillOpen.length > 0 ? stillOpen : [...openForSelection],
      inputMode: "scan",
    };
  }

  const refreshed = kittingRefreshPickSelection(
    params.scopedPickLines,
    params.selectedLine,
    params.candidateLines,
  );
  if (refreshed.selectedLine == null && refreshed.candidateLines.length === 0) {
    return {
      selectedLine: null,
      selectedSku: null,
      selectedStorageLocation: null,
      candidateLines: [],
      inputMode: "scan",
    };
  }
  return {
    selectedLine: refreshed.selectedLine,
    selectedSku: refreshed.selectedLine?.sku ?? sku,
    selectedStorageLocation: refreshed.selectedLine?.storageLocation ?? location,
    candidateLines: refreshed.candidateLines,
    inputMode: refreshed.inputMode,
  };
}

export type KittingPickLineQuantities = {
  needed: number;
  allocated: number;
  left: number;
};

/** Rounded quantity columns for kitting pick line tiles (needed / allocated / left). */
export function kittingPickLineQuantities(line: KittingPickLine): KittingPickLineQuantities {
  return {
    needed: Math.max(0, Math.round(line.qtyNeeded)),
    allocated: Math.max(0, Math.round(line.qtyAllocated)),
    left: Math.max(0, Math.round(line.qtyDue)),
  };
}

export interface KittingSkuPickGroup {
  sku: string;
  lines: KittingPickLine[];
  totalDue: number;
}

/** Distinct SKUs across pick lines for kiosk line tiles. */
export function kittingUniqueSkuGroups(lines: readonly KittingPickLine[]): KittingSkuPickGroup[] {
  const bySku = new Map<string, KittingPickLine[]>();
  for (const line of lines) {
    const key = line.sku.trim().toLowerCase();
    const list = bySku.get(key) ?? [];
    list.push(line);
    bySku.set(key, list);
  }
  return Array.from(bySku.values())
    .map((group) => ({
      sku: group[0]!.sku,
      lines: group,
      totalDue: group.reduce((sum, line) => sum + Math.max(0, Math.round(line.qtyDue)), 0),
    }))
    .sort((left, right) => left.sku.localeCompare(right.sku, undefined, { sensitivity: "base" }));
}

export interface KittingSkuLocationPickGroup {
  sku: string;
  storageLocation: string;
  lines: KittingPickLine[];
}

function kittingSkuLocationGroupKey(sku: string, storageLocation: string): string {
  return `${sku.trim().toLowerCase()}\0${storageLocation}`;
}

/** One kiosk tile per SKU stop (storage sublocation), ordered by location then SKU. */
export function kittingUniqueSkuLocationGroups(
  lines: readonly KittingPickLine[],
): KittingSkuLocationPickGroup[] {
  const byKey = new Map<string, KittingPickLine[]>();
  for (const line of lines) {
    const key = kittingSkuLocationGroupKey(line.sku, line.storageLocation);
    const list = byKey.get(key) ?? [];
    list.push(line);
    byKey.set(key, list);
  }
  return Array.from(byKey.values())
    .map((group) => ({
      sku: group[0]!.sku,
      storageLocation: group[0]!.storageLocation,
      lines: group,
    }))
    .sort((left, right) => {
      const loc = left.storageLocation.localeCompare(right.storageLocation, undefined, {
        sensitivity: "base",
      });
      if (loc !== 0) return loc;
      return left.sku.localeCompare(right.sku, undefined, { sensitivity: "base" });
    });
}

export function kittingPickLinesForDestinationSlot(
  pickLines: readonly KittingPickLine[],
  destinationSlotId: string,
): KittingPickLine[] {
  return pickLines.filter((line) => line.destinationSlotId === destinationSlotId);
}

export function kittingPickLineForCartScan(
  scanned: string,
  params: {
    activeSlotIds: readonly string[];
    letterToSlotKey?: ReadonlyMap<string, string>;
    candidateLines: readonly KittingPickLine[];
    scopedPickLines: readonly KittingPickLine[];
    selectedSku: string | null;
    selectedStorageLocation: string | null;
  },
): { resolution: KittingCartScanResolution; line: KittingPickLine } | null {
  const resolution = resolveKittingCartScanToSlotKey(
    scanned,
    params.activeSlotIds,
    params.letterToSlotKey,
  );
  if (resolution == null) return null;

  const pool =
    params.candidateLines.length > 0 && params.selectedSku != null
      ? params.candidateLines
      : params.scopedPickLines;

  const slotLines = kittingPickLinesForDestinationSlot(pool, resolution.slotKey);
  if (slotLines.length === 0) return null;

  if (params.selectedSku != null) {
    const skuNeedle = normalizeKittingScannedSku(params.selectedSku).toLowerCase();
    const skuMatches = slotLines.filter((line) => line.sku.trim().toLowerCase() === skuNeedle);
    if (skuMatches.length === 0) return null;
    if (params.selectedStorageLocation != null) {
      const atLocation = skuMatches.filter(
        (line) => line.storageLocation === params.selectedStorageLocation,
      );
      if (atLocation.length === 1) {
        return { resolution, line: atLocation[0]! };
      }
      if (atLocation.length > 1) {
        return { resolution, line: atLocation[0]! };
      }
      return null;
    }
    if (skuMatches.length === 1) {
      return { resolution, line: skuMatches[0]! };
    }
    return null;
  }

  if (slotLines.length === 1) {
    return { resolution, line: slotLines[0]! };
  }

  return null;
}

/**
 * Carton scans that loaded a real box but cannot be picked.
 * Returns null when the carton matches an open line.
 */
export function kittingInventoryBoxScanError(input: {
  sku: string;
  shortCode: string;
  archived: boolean;
  matchingOpenLineCount: number;
  midPick?: { runId: number; slotKey: string } | null;
}): string | null {
  const sku = input.sku.trim();
  const shortCode = input.shortCode.trim();
  if (input.midPick != null) {
    const label = shortCode === "" ? "That inventory container" : `Carton ${shortCode}`;
    return `${label} is already mid-pick on run ${String(input.midPick.runId)}, cart bin ${input.midPick.slotKey}. It is still in the warehouse.`;
  }
  if (input.archived) {
    return shortCode === ""
      ? "That inventory container is archived."
      : `Carton ${shortCode} is archived.`;
  }
  if (input.matchingOpenLineCount > 0) return null;
  if (shortCode !== "" && sku !== "") {
    return `Carton ${shortCode} is SKU ${sku}. There is no open pick line for that SKU.`;
  }
  if (sku !== "") {
    return `This carton is SKU ${sku}. There is no open pick line for that SKU.`;
  }
  return "This carton is not on an open pick line.";
}
